import { useState } from "react";
import { DEPARTMENT_PAGE_SIZE_OPTIONS } from "../assets/instateamMasterConfig";
import {
  AppIcon,
  LabeledInput,
  MetricCard,
} from "../components/InstateamMasterComponents";
import { getInitials, toAbsoluteUrl } from "../utils/adminHelpers";

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

function getDepartmentVisual(name) {
  const normalizedName = String(name || "").trim().toLowerCase();

  if (normalizedName.includes("develop") || normalizedName.includes("engineer")) {
    return { icon: "code", tone: "gold" };
  }

  if (normalizedName.includes("market")) {
    return { icon: "announcements", tone: "violet" };
  }

  if (normalizedName.includes("hr") || normalizedName.includes("human")) {
    return { icon: "employees", tone: "blue" };
  }

  if (normalizedName.includes("finance") || normalizedName.includes("account")) {
    return { icon: "payroll", tone: "red" };
  }

  if (normalizedName.includes("support")) {
    return { icon: "support", tone: "cyan" };
  }

  if (normalizedName.includes("sale")) {
    return { icon: "reports", tone: "amber" };
  }

  if (normalizedName.includes("design")) {
    return { icon: "palette", tone: "peach" };
  }

  if (normalizedName.includes("admin")) {
    return { icon: "shieldLock", tone: "slate" };
  }

  return { icon: "departments", tone: "gold" };
}

function DepartmentFormModal({
  isOpen,
  form,
  onFormChange,
  onClose,
  onSubmit,
  isSaving,
  editingDepartment,
  employees,
}) {
  if (!isOpen) return null;

  const effectiveDepartmentKey = String(form.name || editingDepartment?.name || "")
    .trim()
    .toLowerCase();

  const eligibleEmployees = employees.filter((employee) => {
    const employeeDepartmentKey = String(employee.department || "")
      .trim()
      .toLowerCase();

    if (!effectiveDepartmentKey) {
      return employee.fullName === form.departmentHeadName;
    }

    return (
      employee.status === "active" &&
      (employeeDepartmentKey === effectiveDepartmentKey ||
        employee.fullName === form.departmentHeadName)
    );
  });

  const headOptions = Array.from(
    new Set(
      eligibleEmployees
        .map((employee) => employee.fullName)
        .concat(form.departmentHeadName ? [form.departmentHeadName] : [])
        .filter(Boolean)
    )
  ).sort((left, right) => left.localeCompare(right));

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="department-modal">
        <div className="employee-modal-header">
          <div className="employee-modal-title">
            <h3>{editingDepartment ? "Edit Department" : "Add Department"}</h3>
            <p>
              {editingDepartment
                ? "Update department details, department head, and current status."
                : "Create a department that can be used across the employee management system."}
            </p>
          </div>

          <button className="icon-button" type="button" onClick={onClose}>
            <AppIcon name="close" />
          </button>
        </div>

        <form className="department-modal-form" onSubmit={onSubmit}>
          <div className="department-modal-grid">
            <LabeledInput
              label="Department Name"
              required
              value={form.name}
              onChange={(value) => onFormChange((current) => ({ ...current, name: value }))}
              placeholder="Enter department name"
            />

            <label className="form-field">
              <span>
                Department Head
              </span>
              <div className="field-shell select-shell">
                <select
                  value={form.departmentHeadName}
                  onChange={(event) => {
                    const nextHeadName = event.target.value;
                    const matchingEmployee = employees.find(
                      (employee) => employee.fullName === nextHeadName
                    );

                    onFormChange((current) => ({
                      ...current,
                      departmentHeadName: nextHeadName,
                      departmentHeadTitle:
                        matchingEmployee?.designation || current.departmentHeadTitle,
                    }));
                  }}
                >
                  <option value="">Select department head</option>
                  {headOptions.map((employeeName) => (
                    <option key={employeeName} value={employeeName}>
                      {employeeName}
                    </option>
                  ))}
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <LabeledInput
              label="Head Title"
              value={form.departmentHeadTitle}
              onChange={(value) =>
                onFormChange((current) => ({ ...current, departmentHeadTitle: value }))
              }
              placeholder="Enter head designation"
            />

            <label className="form-field">
              <span>
                Status
              </span>
              <div className="field-shell select-shell">
                <select
                  value={form.status}
                  onChange={(event) =>
                    onFormChange((current) => ({ ...current, status: event.target.value }))
                  }
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
                <AppIcon name="chevronDown" />
              </div>
            </label>

            <label className="form-field department-form-wide">
              <span>
                Description <em>*</em>
              </span>
              <div className="textarea-shell">
                <textarea
                  value={form.description}
                  onChange={(event) =>
                    onFormChange((current) => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                  placeholder="Enter department description"
                  rows={5}
                />
              </div>
            </label>
          </div>

          <div className="form-footer">
            <span className="required-note">* Marked fields are required</span>
            <div className="form-footer-actions">
              <button className="secondary-button" type="button" onClick={onClose}>
                Cancel
              </button>
              <button className="primary-button save-button" type="submit">
                {isSaving
                  ? "Saving..."
                  : editingDepartment
                    ? "Save Changes"
                    : "Save Department"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function DepartmentsPage({
  departments,
  employees,
  metrics,
  searchText,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  onClearFilters,
  onAddDepartment,
  onEditDepartment,
  onToggleDepartmentStatus,
  currentPage,
  totalPages,
  onPageChange,
  pageSize,
  onPageSizeChange,
  resultsCount,
  isLoadingDepartments,
  isDepartmentModalOpen,
  departmentForm,
  onDepartmentFormChange,
  onCloseDepartmentModal,
  onSubmitDepartment,
  isSavingDepartment,
  editingDepartment,
}) {
  const [openActionMenuId, setOpenActionMenuId] = useState(null);

  return (
    <section className="departments-page">
      <div className="metrics-grid">
        <MetricCard
          label="Total Departments"
          value={metrics.totalDepartments}
          helper="All departments"
          icon="departments"
        />
        <MetricCard
          label="Total Employees"
          value={metrics.totalEmployees}
          helper="Across all departments"
          icon="employees"
          tone="success"
        />
        <MetricCard
          label="Active Departments"
          value={metrics.activeDepartments}
          helper="Currently active"
          icon="activeUsers"
        />
        <MetricCard
          label="Inactive Departments"
          value={metrics.inactiveDepartments}
          helper="Currently inactive"
          icon="inactiveUsers"
          tone="danger"
        />
      </div>

      <div className="table-card department-table-card">
        <div className="department-page-toolbar">
          <div className="department-page-copy">
            <strong>Department Directory</strong>
            <span>Manage department structure, heads, descriptions, and active state.</span>
          </div>

          <button className="primary-button add-button" type="button" onClick={onAddDepartment}>
            <AppIcon name="plus" />
            Add Department
          </button>
        </div>

        <div className="department-filter-row">
          <label className="inline-search-field">
            <AppIcon name="search" />
            <input
              value={searchText}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder="Search departments..."
            />
          </label>

          <div className="department-filter-actions">
            <label className="inline-select-shell">
              <select
                value={statusFilter}
                onChange={(event) => onStatusFilterChange(event.target.value)}
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>

            <button className="secondary-icon-button" type="button" onClick={onClearFilters}>
              <AppIcon name="filter" />
            </button>
          </div>
        </div>

        <div className="table-wrapper">
          <table className="department-table">
            <thead>
              <tr>
                <th>Department</th>
                <th>Department Head</th>
                <th>Employees</th>
                <th>Description</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              {isLoadingDepartments ? (
                <tr>
                  <td colSpan="6" className="empty-table-state">
                    Loading departments...
                  </td>
                </tr>
              ) : null}

              {!isLoadingDepartments && !departments.length ? (
                <tr>
                  <td colSpan="6" className="empty-table-state">
                    No departments found. Add the first department to get started.
                  </td>
                </tr>
              ) : null}

              {!isLoadingDepartments
                ? departments.map((department) => {
                    const visual = getDepartmentVisual(department.name);
                    const departmentHeadEmployee = employees.find(
                      (employee) => employee.fullName === department.departmentHeadName
                    );

                    return (
                      <tr key={department._id}>
                        <td>
                          <div className="department-name-cell">
                            <div className={`department-icon-chip ${visual.tone}`}>
                              <AppIcon name={visual.icon} />
                            </div>

                            <div className="department-name-copy">
                              <strong>{department.name}</strong>
                              <span>{department.departmentHeadTitle || "Department"}</span>
                            </div>
                          </div>
                        </td>

                        <td>
                          <div className="department-head-cell">
                            {departmentHeadEmployee?.profilePhoto?.url ? (
                              <img
                                src={toAbsoluteUrl(departmentHeadEmployee.profilePhoto.url)}
                                alt={department.departmentHeadName}
                                className="table-avatar"
                              />
                            ) : (
                              <div className="avatar-circle table-avatar">
                                {getInitials(department.departmentHeadName || department.name)}
                              </div>
                            )}

                            <div className="employee-cell-copy">
                              <strong>{department.departmentHeadName || "Not Assigned"}</strong>
                              <span>
                                {department.departmentHeadTitle ||
                                  departmentHeadEmployee?.designation ||
                                  "No department head selected"}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td>{department.employeeCount}</td>
                        <td className="department-description-cell">
                          {department.description || "No description added yet."}
                        </td>
                        <td>
                          <span
                            className={`status-badge ${
                              department.status === "active" ? "active" : "inactive"
                            }`}
                          >
                            {department.status === "active" ? "Active" : "Inactive"}
                          </span>
                        </td>
                        <td>
                          <div className="row-actions">
                            <button
                              className="action-icon-button"
                              type="button"
                              onClick={() => onEditDepartment(department)}
                            >
                              <AppIcon name="edit" />
                            </button>

                            <div className="row-action-shell">
                              <button
                                className="action-icon-button"
                                type="button"
                                onClick={() =>
                                  setOpenActionMenuId((current) =>
                                    current === department._id ? null : department._id
                                  )
                                }
                              >
                                <AppIcon name="more" />
                              </button>

                              {openActionMenuId === department._id ? (
                                <div className="row-action-menu">
                                  <button
                                    type="button"
                                    onClick={() => {
                                      setOpenActionMenuId(null);
                                      onToggleDepartmentStatus(department);
                                    }}
                                  >
                                    {department.status === "active"
                                      ? "Mark Inactive"
                                      : "Mark Active"}
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                : null}
            </tbody>
          </table>
        </div>

        <div className="table-footer department-table-footer">
          <span>
            Showing {resultsCount ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
            {Math.min(currentPage * pageSize, resultsCount)} of {resultsCount} departments
          </span>

          <div className="department-footer-actions">
            <div className="pagination">
              <button
                className="pagination-arrow"
                type="button"
                onClick={() => onPageChange(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
              >
                <AppIcon name="chevronLeft" />
              </button>

              {Array.from({ length: totalPages }, (_, index) => index + 1)
                .slice(0, Math.min(totalPages, 5))
                .map((page) => (
                  <button
                    key={page}
                    className={`page-chip ${page === currentPage ? "active" : ""}`}
                    type="button"
                    onClick={() => onPageChange(page)}
                  >
                    {page}
                  </button>
                ))}

              <button
                className="pagination-arrow"
                type="button"
                onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
              >
                <AppIcon name="chevronRight" />
              </button>
            </div>

            <label className="page-size-shell">
              <select
                value={pageSize}
                onChange={(event) => onPageSizeChange(Number(event.target.value))}
              >
                {DEPARTMENT_PAGE_SIZE_OPTIONS.map((size) => (
                  <option key={size} value={size}>
                    {size} / page
                  </option>
                ))}
              </select>
              <AppIcon name="chevronDown" />
            </label>
          </div>
        </div>
      </div>

      <DepartmentFormModal
        isOpen={isDepartmentModalOpen}
        form={departmentForm}
        onFormChange={onDepartmentFormChange}
        onClose={onCloseDepartmentModal}
        onSubmit={onSubmitDepartment}
        isSaving={isSavingDepartment}
        editingDepartment={editingDepartment}
        employees={employees}
      />
    </section>
  );
}
